/**
 * What the frame lets the app inside do: the Flutter admin's `EmbeddedWebView`
 * (webviewx_plus 0.5.0 `WebSpecificParams`), so an app that copies to the clipboard, goes
 * full screen, plays protected media or opens a popup does here what it did there. Its
 * sandbox lifts everything but top navigation, so the app inside cannot send the admin away.
 */
import { useState } from 'react';
import { Shimmer } from '@tutero/ui';

const ALLOW =
  'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
const SANDBOX = [
  'allow-downloads',
  'allow-forms',
  'allow-modals',
  'allow-orientation-lock',
  'allow-pointer-lock',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-presentation',
  'allow-same-origin',
  'allow-scripts',
].join(' ');

/**
 * Another app inside the admin (`the-nav-lists-every-tool` D5): one frame filling the
 * page, titled for a screen reader, making no call of ours (A2). These are Tutero's own
 * apps and they sign their own staff in; the frame grants them what Flutter's did (above).
 *
 * It takes the whole area beside the nav, edge to edge: `data-slot="embed"` is what the
 * shell's column reads to drop its gutters for it (`AppSidebar`), so the app inside gets
 * the frame rather than a letterboxed box inside the page's padding. No radius and no
 * border, which a page of its own has neither of; no aspect ratio, so it follows the
 * window and the nav collapsing on its own.
 *
 * Until the app inside has loaded, a shimmer shaped like a page takes the frame's place beside
 * it, and the frame, still loading, is hidden; it fades in once its `load` fires. Which address
 * has loaded is kept rather than a flag, since moving from one embed to another reuses this frame.
 */
export function Embed({ title, src }: { title: string; src: string }) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const loaded = loadedSrc === src;
  return (
    <>
      {loaded ? null : <EmbedShimmer title={title} />}
      <iframe
        data-slot="embed"
        title={title}
        src={src}
        allow={ALLOW}
        allowFullScreen
        sandbox={SANDBOX}
        onLoad={() => setLoadedSrc(src)}
        className={`min-h-0 w-full flex-1 border-0 ${loaded ? 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300' : 'hidden'}`}
      />
    </>
  );
}

/** A page coming, in the page's own gutters: a heading, a line beneath it, and a few blocks. */
function EmbedShimmer({ title }: { title: string }) {
  return (
    <div
      role="status"
      aria-label={`Loading ${title}`}
      className="flex min-h-0 w-full flex-1 flex-col gap-4 p-6"
    >
      <Shimmer className="h-7 w-1/3 rounded-lg" />
      <Shimmer className="h-4 w-2/3 rounded" />
      <Shimmer className="h-24 w-full rounded-xl" />
      <Shimmer className="h-24 w-full rounded-xl" />
      <Shimmer className="h-24 w-full rounded-xl" />
    </div>
  );
}
